import { useRef } from 'react'

import { ServerStatus } from '../schema/server-status.schema'

type DirtyFieldsType =
  | boolean
  | null
  | {
      [key: string]: DirtyFieldsType
    }
  | Array<DirtyFieldsType>

function getDirtyValues<T extends Record<string, any>>(
  dirtyFields: Partial<Record<keyof T, DirtyFieldsType>>,
  values: T
): Partial<T> {
  const dirtyValues = Object.keys(dirtyFields).reduce((prev, key) => {
    const value = dirtyFields[key]
    if (!value) {
      return prev
    }
    const isObject = typeof value === 'object'
    const isArray = Array.isArray(value)
    const nestedValue =
      isObject && !isArray
        ? getDirtyValues(value as Record<string, any>, values[key])
        : values[key]
    return { ...prev, [key]: isArray ? values[key] : nestedValue }
  }, {} as Partial<T>)
  return dirtyValues
}

function canEdit(status?: ServerStatus | null) {
  return status === 'draft' || status === 'pending'
}

async function insertPlaceholder(
  {
    placeholder,
    textAreaRef,
  }: {
    placeholder: string
    textAreaRef: React.RefObject<HTMLIonTextareaElement>
  },
  callback: (newValue: string) => void
) {
  if (!textAreaRef.current)
    throw new Error('The textarea was not initialized properly.')

  const textArea = await textAreaRef.current.getInputElement()

  const cursorPosition = textArea.selectionStart || 0
  const newPosition = cursorPosition + placeholder.length

  // split current value
  const start = textArea.value.slice(0, cursorPosition)
  const end = textArea.value.slice(cursorPosition)

  const newString = `${start}${placeholder}${end}`

  // callback to update textarea in ui
  callback(newString)

  await textAreaRef.current.setFocus()

  // reset cursor position after placeholder
  textArea.setSelectionRange(newPosition, newPosition)
}

function useTextAreaRef() {
  return useRef<HTMLIonTextareaElement>(null)
}

export { canEdit, getDirtyValues, insertPlaceholder, useTextAreaRef }
